import { useEffect, useState } from 'react'
import _ from 'lodash'
import { Input, Grid, Button, Separator } from '@design-systems/canvas-ui'
import { renderControl } from '../Builders/centralControlBuilder'
import Icon, {
  PlusCircleIcon,
  MinusCircleIcon,
} from '@design-systems/react-icons'
import {
  renderControlLabel,
  showErrorMessage,
  renderControlLabelSmallHeaderOnly,
} from '../Builders'
import { buildDropdownOptions } from '../../common/helper'
import { getControlError } from '../../common/helper'

const LABEL_FROM_MONTH = 'From Month'
const LABEL_TO_MONTH = 'To Month'

const controlGroups = [
  {
    subSectionReference: 'factory_capability',
    controls: [
      {
        uid: 'daily_factory_output',
        type: 'number_box',
        label_name: '</br>Daily Factory Output (units)',
        is_mandatory: true,
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
        decimals: 1,
      },
      {
        uid: 'automation_level',
        type: 'autocomplete_others',
        options: 'rfi_location.automation_level',
        label_name: '</br> Automation Level',
        is_mandatory: true,
        row: 1,
        column: 2,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'capacity_percentage_open_for_target',
        type: 'percentage_box',
        label_name: '</br> Percent of Capacity open for Target',
        is_mandatory: true,
        row: 1,
        column: 3,
        width: 3,
        width_percentage: 94,
      },
      {
        uid: 'capacity_percentage_penetrated_by_other_customers',
        type: 'percentage_box',
        label_name: 'Percent of capacity penetrated by other </br> customers',
        is_mandatory: true,
        row: 1,
        column: 4,
        width: 3,
        width_percentage: 94,
      },
    ],
  },
]

const controlGroupsForReadOnly = [
  {
    subSectionReference: 'factory_capability',
    controls: [
      {
        uid: 'daily_factory_output',
        type: 'label_readonly_number',
        label_name: '</br>Daily Factory Output (units)',
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'automation_level',
        type: 'label_readonly_header',
        label_name: '</br> Automation Level',
        row: 1,
        column: 2,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'capacity_percentage_open_for_target',
        type: 'label_readonly_header',
        label_name: '</br> Percent of Capacity open for Target',
        row: 1,
        column: 3,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'capacity_percentage_penetrated_by_other_customers',
        type: 'label_readonly_header',
        label_name: 'Percent of capacity penetrated by other </br> customers',
        row: 1,
        column: 4,
        width: 3,
        width_percentage: 100,
      },
    ],
  },
]

const FactoryHierarchyDetails = (props) => {
  const [hierarchyDetail, setHierarchyDetail] = useState()

  useEffect(() => {
    if (!_.isEqual(props.hierarchyDetail, hierarchyDetail)) {
      props.updateFCDetail(
        props.FCIndex,
        props.subSectionReferenceKey,
        hierarchyDetail,
      )
    }
  }, [hierarchyDetail]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHierarchyDetail(props.hierarchyDetail)
     
  }, [props.hierarchyDetail])

  const onInputChangeHandler = (value, updateMetadata) => {
    setHierarchyDetail({
      ...hierarchyDetail,
      [updateMetadata.uid]: value,
    })
  }

  const renderTPCGroupDetails = () => {
    const controlGroupsForComputation = props.isReadonly
      ? controlGroupsForReadOnly
      : controlGroups
    return controlGroupsForComputation.map((eachTPCGroup, tpcIndex) => {
      let groupData = hierarchyDetail
      let groupControls = []
      let groupReferencePath = `${props.sectionTemplatePath}.${eachTPCGroup.subSectionReference}`

      eachTPCGroup.controls.forEach((eachControl) => {
        if (eachControl) {
          let mutatedControl = Object.assign({}, eachControl)
          let controlMetadata = {
            client: 'FactoryHierarchyDetails',
            sectionPath: groupReferencePath,
            errorCache: props.errorCache,
          }
          mutatedControl.metadata = controlMetadata

          groupControls.push(
            <Grid.Item size={{ xs: eachControl.width }}>
              {renderControl(
                mutatedControl,
                groupData,
                props.rfiMasterData,
                props.isReadonly ? () => {} : onInputChangeHandler,
              )}
            </Grid.Item>,
          )
        }
      })

      return (
        <div key={`div-tpc-group-${tpcIndex}`}>
          <Input.Label className="hc8-fs-150 font-weight-500 hc8-pt-none">
            {eachTPCGroup.group}
          </Input.Label>
          <Grid.Container className="hc8-mt-none">
            {groupControls}
          </Grid.Container>
        </div>
      )
    })
  }

  const updateDSRowProperty = (group, property, rowIndex, newVal) => {
    let updatedDCGroupRows = hierarchyDetail[group].map((eachRow, index) => {
      return rowIndex === index
        ? {
            ...eachRow,
            [property]: newVal ? newVal : null,
          }
        : eachRow
    })
    setHierarchyDetail({
      ...hierarchyDetail,
      [group]: updatedDCGroupRows,
    })
  }

  const renderFromToMonthGroup = () => {
    let designManufacturingPackagingSeason =
      hierarchyDetail?.manufacturing_packaging_season?.map(
        (eachMPSRow, eachMPSIndex) => {
          let mpsFromMonthIndex = getControlError(
            `${props.sectionTemplatePath}
            ${'.factory_capability.manufacturing_packaging_season'}[${eachMPSIndex}].from_month`,
            props.errorCache,
          )
          let mpsToMonthIndex = getControlError(
            `${props.sectionTemplatePath}
            ${'.factory_capability.manufacturing_packaging_season'}[${eachMPSIndex}].to_month`,
            props.errorCache,
          )

          return (
            <div key={`div-from-to-month-group-${eachMPSIndex}`}>
              <Grid.Container
                className="hc8-mt-4x background-grey border-radius"
                spacing="none"
              >
                {/* 1. From Month */}
                <Grid.Item size={{ xs: 3 }} className="hc8-pl-3x hc8-pt-3x">
                  <div style={{ width: '100%' }}>
                    {props.isReadonly
                      ? renderControlLabelSmallHeaderOnly({
                          label_name: LABEL_FROM_MONTH,
                        })
                      : renderControlLabel({ label_name: LABEL_FROM_MONTH })}
                    {props.isReadonly ? (
                      <Input.Label
                        key={`label-from-month`}
                        className="hc8-mt-2x"
                        style={{ width: '80%' }}
                      >
                        {eachMPSRow.from_month ? eachMPSRow.from_month : ''}
                      </Input.Label>
                    ) : (
                      <>
                        <Input.Select
                          key={`dropdown-from-month`}
                          className="hc8-mt-2x"
                          isErrored={mpsFromMonthIndex}
                          style={{ width: '80%' }}
                          options={buildDropdownOptions(
                            props.rfiMasterData?.common?.month_list,
                            null,
                            true,
                            true,
                          )}
                          value={
                            eachMPSRow.from_month ? eachMPSRow.from_month : ''
                          }
                          onChange={(option) => {
                            updateDSRowProperty(
                              'manufacturing_packaging_season',
                              'from_month',
                              eachMPSIndex,
                              option?.id,
                            )
                          }}
                        />
                        <div className="hc8-pt-2x">
                          {showErrorMessage(mpsFromMonthIndex)}
                        </div>
                      </>
                    )}
                  </div>
                </Grid.Item>

                {/* 2. To Month */}
                <Grid.Item size={{ xs: 3 }} className="hc8-pl-4x hc8-ml-4x hc8-pt-3x">
                  <div style={{ width: '100%' }}>
                    {props.isReadonly
                      ? renderControlLabelSmallHeaderOnly({
                          label_name: LABEL_TO_MONTH,
                        })
                      : renderControlLabel({ label_name: LABEL_TO_MONTH })}
                    {props.isReadonly ? (
                      <Input.Label
                        key={`label-to-month`}
                        className="hc8-mt-2x"
                        style={{ width: '80%' }}
                      >
                        {eachMPSRow.to_month ? eachMPSRow.to_month : ''}
                      </Input.Label>
                    ) : (
                      <>
                        <Input.Select
                          key={`dropdown-to-month`}
                          className="hc8-mt-2x"
                          isErrored={mpsToMonthIndex}
                          style={{ width: '80%' }}
                          options={buildDropdownOptions(
                            props.rfiMasterData?.common?.month_list,
                            null,
                            true,
                            true,
                          )}
                          value={eachMPSRow.to_month ? eachMPSRow.to_month : ''}
                          onChange={(option) => {
                            updateDSRowProperty(
                              'manufacturing_packaging_season',
                              'to_month',
                              eachMPSIndex,
                              option?.id,
                            )
                          }}
                        />
                        <div className="hc8-pt-2x">
                          {showErrorMessage(mpsToMonthIndex)}
                        </div>
                      </>
                    )}
                  </div>
                </Grid.Item>
                {!props.isReadonly && (
                  <Grid.Item size={{ xs: 1 }} className="align-centre-left hc8-pt-4x">
                    <Button
                      key={`delete-ds-row-${eachMPSIndex}`}
                      id={`delete-ds-row-${eachMPSIndex}`}
                      data-testid={`delete-ds-row-${eachMPSIndex}`}
                      onClick={() => {
                        let data = [
                          ...hierarchyDetail.manufacturing_packaging_season,
                        ]
                        data.splice(eachMPSIndex, 1)
                        setHierarchyDetail({
                          ...hierarchyDetail,
                          manufacturing_packaging_season: data,
                        })
                      }}
                      variant="ghost"
                      size="sm"
                      isIconOnly
                      className="hc8-ml-6x"
                      aria-label="Delete design service row"
                    >
                      <Icon svg={MinusCircleIcon} aria-hidden />
                    </Button>
                  </Grid.Item>
                )}
              </Grid.Container>
            </div>
          )
        },
      )

    return (
      <div>
        <Input.Label className="hc8-fs-150 font-weight-500 hc8-mt-2x">
          Manufacturing/Packing Season (Choose “From Month” - “To Month”).
        </Input.Label>
        {designManufacturingPackagingSeason}
        <Separator className="hc8-pt-3x" />
        {!props.isReadonly && (
          <Button
            key="btn-add-ds-row"
            id="btn-add-ds-row"
            data-testid="btn-add-ds-row"
            variant="ghost"
            className="hc8-pl-none hc8-mt-3x"
            onClick={() => addNewMaterialPackagingSeasonRow()}
          >
            <Icon svg={PlusCircleIcon} className="hc8-mr-2x" aria-hidden />
            Add More
          </Button>
        )}
      </div>
    )
  }

  /**
   * Add Manufacturing/Packaging Season row
   */
  const addNewMaterialPackagingSeasonRow = () => {
    setHierarchyDetail({
      ...hierarchyDetail,
      manufacturing_packaging_season: [
        ...hierarchyDetail.manufacturing_packaging_season,
        {
          from_month: '',
          to_month: '',
        },
      ],
    })
  }

  return (
    <Grid.Container>
      <Grid.Item size={{ xs: 12 }} className="hc8-pv-none">
        {hierarchyDetail && renderTPCGroupDetails()}
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        {hierarchyDetail?.product_type?.toLowerCase() === 'grocery' &&
          renderFromToMonthGroup()}
      </Grid.Item>
    </Grid.Container>
  )
}

export default FactoryHierarchyDetails
